import { parseLevelId, type LevelId, type WorldId } from './ids';

// The tuning table: the only place with the numbers of the GDD. The core imports them from here.

/** Simulation rate: fixed step of 1/60 s (GDD §5.3, §15.4). */
export const SIM_HZ = 60;

/** Cap of simulation steps per rendered frame, against the spiral of death (GDD §15.4). */
export const MAX_STEPS_PER_FRAME = 5;

/** Fixed simulation step in seconds (GDD §5.3). */
export const SIM_DT = 1 / SIM_HZ;

/** Gravity in u/s² (GDD §5.3). */
export const GRAVITY = 30;

/** A flap sets vy to this value in u/s; it replaces vy, never adds to it (GDD §5.3). */
export const FLAP_IMPULSE = 9;

/** Lowest vertical speed in u/s: the fall never gets faster (GDD §5.3). */
export const MAX_FALL_SPEED = -14;

/** Visual pitch: clamp(vy × 4°, −60°, +25°). Render only, never part of collisions (GDD §5.3). */
export const PITCH_DEGREES_PER_UNIT = 4;
export const PITCH_MIN = -60;
export const PITCH_MAX = 25;

/** Player hitbox: axis-aligned box centred on the fuselage, in u (GDD §4.1). */
export const PLAYER_HITBOX = { width: 1.2, height: 0.5 } as const;

/** Visible height of the 16:9 view, y from 0 to 12 (GDD §5.2). */
export const VIEW_HEIGHT = 12;

/** Top of the playable band: the ceiling does not kill (GDD §5.2). */
export const WORLD_TOP = VIEW_HEIGHT;

/** Ground height: touching it is a damaging collision (GDD §5.2). */
export const GROUND_Y = 1;

/** Pickups (fajos and items) are collected with a generous circle of this radius, in u (GDD §7). */
export const PICKUP_RADIUS = 0.6;

/** The plane stays at this fraction of the visible width (GDD §5.2). */
export const PLAYER_SCREEN_X = 0.3;

/** Lives (GDD §6.1). */
export const START_LIVES = 3;
export const MAX_LIVES = 9;
export const DOLLARS_PER_EXTRA_LIFE = 100;

/** Assist mode (GDD §13.6, issue #138): the scroll at 80 %, the gaps 15 % taller and 5 lives to start; the level data is not changed. */
export const ASSIST = Object.freeze({ scrollFactor: 0.8, gapFactor: 1.15, startLives: 5 });

/** Invulnerability after respawning, in seconds (GDD §6.1). */
export const RESPAWN_INVULNERABILITY_S = 2.0;

/** Armor plate (GDD §7, issue #79): invulnerability after it absorbs a hit, and the bonus of a second plate. */
export const ARMOR_INVULNERABILITY_S = 1.5;
export const ARMOR_DUPLICATE_BONUS = 500;
/** Proposal @gdd-pending: the armor also absorbs the ground (literal reading of GDD §6.1). */
export const ARMOR_PROTECTS_GROUND = true;

export interface WorldTuning {
  /** Scroll speed of level 1 of the world, in u/s. */
  readonly scrollSpeedLevel1: number;
  readonly gapHeight: number;
  readonly columnSpacing: number;
  /** Largest change of the gap centre between consecutive columns. */
  readonly maxGapShift: number;
  readonly columnWidth: number;
}

/** Gap obstacles per world (GDD §5.4). */
export const WORLD_TUNING: Readonly<Record<WorldId, WorldTuning>> = Object.freeze({
  1: { scrollSpeedLevel1: 5.0, gapHeight: 4.0, columnSpacing: 9.0, maxGapShift: 3.0, columnWidth: 1.5 },
  2: { scrollSpeedLevel1: 5.5, gapHeight: 3.7, columnSpacing: 8.5, maxGapShift: 3.5, columnWidth: 1.5 },
  3: { scrollSpeedLevel1: 6.0, gapHeight: 3.5, columnSpacing: 8.0, maxGapShift: 3.5, columnWidth: 1.5 },
  4: { scrollSpeedLevel1: 6.5, gapHeight: 3.2, columnSpacing: 7.0, maxGapShift: 4.0, columnWidth: 1.5 },
});

/** Scroll speed added in levels 1, 2 and 3 of every world (GDD §5.4). */
export const LEVEL_SPEED_INCREMENT: readonly [number, number, number] = [0, 0.25, 0.5];

export function scrollSpeedFor(id: LevelId): number {
  const { world, level } = parseLevelId(id);
  return WORLD_TUNING[world].scrollSpeedLevel1 + LEVEL_SPEED_INCREMENT[level - 1]!;
}

/** The constants of the tuning table by name, for tools and tests. */
export const TUNING = Object.freeze({
  gravity: GRAVITY,
  flapImpulse: FLAP_IMPULSE,
  maxFallSpeed: MAX_FALL_SPEED,
  simulationHz: SIM_HZ,
  maxStepsPerFrame: MAX_STEPS_PER_FRAME,
  playerHitboxWidth: PLAYER_HITBOX.width,
  playerHitboxHeight: PLAYER_HITBOX.height,
  pickupRadius: PICKUP_RADIUS,
  viewHeight: VIEW_HEIGHT,
  groundY: GROUND_Y,
  playerScreenX: PLAYER_SCREEN_X,
  startLives: START_LIVES,
  assist: ASSIST,
  maxLives: MAX_LIVES,
  dollarsPerExtraLife: DOLLARS_PER_EXTRA_LIFE,
  respawnInvulnerability: RESPAWN_INVULNERABILITY_S,
  armorInvulnerability: ARMOR_INVULNERABILITY_S,
  armorDuplicateBonus: ARMOR_DUPLICATE_BONUS,
});

/** Level composition (GDD §10.2): target lengths in u and the tolerance of the validator. */
export const LEVEL_COMPOSITION = Object.freeze({
  /** Length of levels 1 and 2 of every world. */
  flightLevelLength: 600,
  /** Length of the approach of level 3, before the boss arena. */
  approachLength: 400,
  takeoffLength: 40,
  airstripLength: 60,
  usSkyLength: 120,
  /** CP-A of levels 1 and 2 sits at this fraction of the level (GDD §6.2). */
  checkpointAFraction: 0.5,
  /** Tolerance on lengths and on the CP-A position: 5 %. */
  tolerance: 0.05,
  /** Share of the pattern length of each phase of the difficulty curve, with the same tolerance. */
  curve: { warmup: 0.2, tension: 0.3, respite: 0.1, peak: 0.4 },
});

/** Visible width of the 16:9 view: 12 × 16 / 9 = 21.333 u (GDD §5.2). */
export const VIEW_WIDTH = (VIEW_HEIGHT * 16) / 9;

/** Entities take part in the simulation within this distance beyond either edge of the view, in u. */
export const ACTIVATION_MARGIN = 2;

/** Points for each column passed (GDD §12). */
export const COLUMN_POINTS = 10;

/** Points for each dollar bundle collected (GDD §12). */
export const DOLLAR_POINTS = 50;

/** The plane blinks at this rate while invulnerable (GDD §6.1). */
export const BLINK_HZ = 10;

/** Soborno: total invulnerability against enemies, obstacles and projectiles, never the ground (GDD §7). */
export const BRIBE_DURATION_S = 6;

/** Respawn altitude when no column lies ahead: the middle of the band between ground and top (GDD §6.2). */
export const RESPAWN_FALLBACK_Y = (GROUND_Y + WORLD_TOP) / 2;

/** Bonuses (GDD §12): activating a checkpoint and completing a level without losing a life. */
export const CHECKPOINT_POINTS = 200;
export const CLEAN_RUN_POINTS = 2000;

/** El Salto (GDD §11.1, issue #92): the autopilot of the jump zone flies level at this height, reached in 1 s. */
export const JUMP_ZONE = {
  autopilotAltitude: 9,
  autopilotSettleSeconds: 1,
  /** The X lies on the ground this far ahead of the pilot when the jump zone starts (#93). */
  targetAhead: 8,
  /** The jump (§11.1 steps 3-5): parachute after 0.5 s, descent of 2 u/s, 3 u/s forward braking at 1 u/s². */
  parachuteDelaySeconds: 0.5,
  parachuteDescent: -2,
  jumpHorizontalSpeed: 3,
  jumpHorizontalDecel: 1,
  /** The X of the target spans this wide on the ground (u): PERFECTO (0.5 u) reads as its centre (#94). */
  targetSpan: 3,
} as const;

/** Level endings (GDD §12, §11.2): landing on the airstrip (#72), the boss (#93) and the grades of El Salto (#94). */
export const LANDING_POINTS = 500;
export const BOSS_POINTS = 5000;
export type JumpGrade = 'PERFECTO' | 'EXCELENTE' | 'BUENO' | 'REGULAR' | 'FORZOSO';
export const JUMP_GRADE_POINTS: Readonly<Record<JumpGrade, number>> = {
  PERFECTO: 5000,
  EXCELENTE: 2000,
  BUENO: 800,
  REGULAR: 400,
  FORZOSO: 100,
};

/** DERRIBO lasts this long before respawning or failing the operation (GDD §6.2, §13.2). */
export const DERRIBO_DURATION_S = 1.5;
